import { Component } from '@theme/component';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Blends native recommendations for distinct cart products. The native cart owns all cart mutations.
 * @typedef {Object} RecommendationRefs
 * @property {HTMLScriptElement} cartData
 * @property {HTMLElement} carousel
 * @property {HTMLElement} [help]
 * @extends {Component<RecommendationRefs>}
 */
class CartRecommendations extends Component {
  requiredRefs = ['cartData', 'carousel'];
  #listeners;
  #request;
  #owner;
  #observer;
  #refreshQueued = false;
  #generation = 0;
  #cache = new Map();
  #key = '';
  #ready = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#listeners?.abort();
    this.#observer?.disconnect();
    this.#generation++;
    this.#refreshQueued = false;
    this.#cancel();
  }

  #cancel = () => {
    this.#request?.abort();
    this.#request = undefined;
  };

  #bind() {
    this.#cleanup();
    this.#ready = false;
    this.#listeners = new AbortController();
    this.#owner = this.closest('cart-drawer-component');
    const options = { signal: this.#listeners.signal };
    this.#owner?.addEventListener(DialogOpenEvent.eventName, this.#load, options);
    this.#owner?.addEventListener(DialogCloseEvent.eventName, this.#cancel, options);
    this.addEventListener(ThemeEvents.cartUpdate, (event) => {
      if (event.detail?.data?.source !== 'product-form-component') return;
      // Move focus before the added card is removed by the native cart's section morph.
      this.#owner?.querySelector('.cart-drawer__heading')?.focus({ preventScroll: true });
    }, options);
    // The installed native morph can retain this host while replacing its children.
    // Watch only cart metadata, host configuration and a cleared track, never carousel dots/status.
    const source = this.refs.cartData;
    const track = this.refs.carousel.querySelector('[ref="track"]');
    this.#observer = new MutationObserver((records) => {
      const changed = records.some((record) =>
        (record.type === 'attributes' && record.target === this) ||
        record.target === source || source.contains(record.target) ||
        (record.type === 'childList' && record.target === track && record.removedNodes.length > 0 && track.childElementCount === 0) ||
        (record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some((node) => node === source)));
      if (!changed || this.#refreshQueued) return;
      this.#refreshQueued = true;
      const generation = this.#generation;
      queueMicrotask(() => {
        if (this.isConnected && generation === this.#generation) this.updatedCallback();
      });
    });
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-limit', 'data-per-product', 'data-fallback', 'data-url'] });
    if (this.#owner?.querySelector('dialog')?.open) this.#load();
  }

  #data() {
    try {
      const data = JSON.parse(this.refs.cartData.textContent);
      return {
        ...data,
        ids: Array.isArray(data.ids) ? data.ids.map(String) : [],
        seeds: Array.isArray(data.seeds) ? [...new Set(data.seeds.map(String))].slice(0, 4) : [],
      };
    } catch {
      return { ids: [], seeds: [] };
    }
  }

  async #pool(seed, intent, signal) {
    const key = `${seed}:${intent}`;
    if (this.#cache.has(key)) return this.#cache.get(key);
    const url = new URL(this.dataset.url, window.location.href);
    if (url.origin !== window.location.origin) return [];
    url.searchParams.set('section_id', this.dataset.poolSection);
    url.searchParams.set('product_id', seed);
    url.searchParams.set('intent', intent);
    url.searchParams.set('limit', '10');
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error(`Cart recommendations: ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (signal.aborted) return [];
    const pool = doc.querySelector('[data-se-cart-pool][data-performed="true"]');
    if (!pool) throw new Error('Missing recommendation pool');
    const cards = [...pool.children].filter((card) => card instanceof HTMLElement && /^\d+$/.test(card.dataset.productId || ''));
    this.#cache.set(key, cards);
    return cards;
  }

  #load = async () => {
    if (this.#request || this.#ready) return;
    const data = this.#data();
    if (!data.seeds.length) { this.hidden = true; return; }
    const key = JSON.stringify([data, this.dataset.url, this.dataset.fallback]);
    if (key !== this.#key) {
      this.#cache.clear();
      this.#key = key;
    }
    const request = new AbortController();
    this.#request = request;
    const excluded = new Set(data.ids);
    const allowed = (cards) => cards.filter((card) => !excluded.has(card.dataset.productId));
    try {
      const responses = await Promise.allSettled(data.seeds.map(async (seed) => {
        let cards = allowed(await this.#pool(seed, 'complementary', request.signal));
        if (!cards.length && this.dataset.fallback === 'true') cards = allowed(await this.#pool(seed, 'related', request.signal));
        return cards;
      }));
      if (request.signal.aborted || !this.isConnected || this.#request !== request) return;
      const pools = responses.map((response) => response.status === 'fulfilled' ? [...response.value] : []);
      const seen = new Set(data.ids);
      const cards = [];
      const perProduct = Math.max(1, Math.min(3, Number(this.dataset.perProduct) || 2));
      const limit = Math.max(2, Math.min(8, Number(this.dataset.limit) || 6));
      // Round-robin gives every seed a turn, up to the configured per-product quota.
      for (let round = 0; round < perProduct; round++) {
        for (const pool of pools) {
          let card;
          while (pool.length && !card) {
            const candidate = pool.shift();
            if (!seen.has(candidate.dataset.productId)) card = candidate;
          }
          if (!card) continue;
          seen.add(card.dataset.productId);
          cards.push(card.cloneNode(true));
          if (cards.length >= limit) break;
        }
        if (cards.length >= limit) break;
      }
      const track = this.refs.carousel.querySelector('[ref="track"]');
      if (!track) return;
      track.replaceChildren(...cards);
      const editor = this.dataset.editor === 'true';
      this.hidden = cards.length === 0 && !editor;
      this.refs.carousel.hidden = cards.length === 0;
      if (this.refs.help) this.refs.help.hidden = cards.length > 0;
      this.#ready = cards.length > 0 || responses.every((response) => response.status === 'fulfilled');
      await customElements.whenDefined('se-product-carousel');
      if (!this.isConnected || request.signal.aborted) return;
      this.refs.carousel.updatedCallback();
    } catch (error) {
      if (error.name !== 'AbortError' && this.isConnected) this.hidden = true;
    } finally {
      if (this.#request === request) this.#request = undefined;
    }
  };
}

if (!customElements.get('se-cart-recommendations')) customElements.define('se-cart-recommendations', CartRecommendations);
